import { faFlag, faHourglass, faPauseCircle, faQuestionSquare } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { CUSTOM_ICONS } from '@nexelem/icons';
import { NodeFlags as NodeFlagsType } from '../../mappers';
import { useMemo } from 'react';
import { Cube } from './Cube';
import { SimulationSnowflakeIcon } from './SimulationSnowflakeIcon';
import { SimulationNotPublishedIcon } from './SimulationNotPublishedIcon';
import { cn } from '@nexelem/utils';

type NodeFlagsProps = {
  nodeId: number;
  flags: NodeFlagsType;
  className?: string;
};

/**
 * hierarchy of symbols (from most important):
 * not equal (❔icon) - only for multinode
 * not published (∅ icon)
 * frozen (❄️ icon)
 * retooling (special icon)
 * paused (️⏸️ icon)
 * downtime (⏳︎ icon)
 * priority (🚩 icon)
 * warehouse shortage (empty cube icon)
 * overdue (🕒 icon))
 * comment (🗨️ icon)
 **/
export const NodeFlags = ({ flags, nodeId, className }: NodeFlagsProps) => {
  const FlagsComponents = useMemo(
    () =>
      [
        flags.notEqualMultinode && (
          <FontAwesomeIcon className="text-icon-darker" icon={faQuestionSquare} key="notEqualMultinode" />
        ),
        <SimulationNotPublishedIcon key="notPublished" nodeId={nodeId} toPublish={flags.toPublish} />,
        <SimulationSnowflakeIcon frozen={flags.frozen} key="frozen" nodeId={nodeId} />,
        flags.retooling && <CUSTOM_ICONS.MachineRetoolSolid className="size-4 text-icon-darker" key="retooling" />,
        flags.paused && <FontAwesomeIcon className="text-icon-darker" icon={faPauseCircle} key="paused" />,
        flags.downtime && <FontAwesomeIcon className="size-4 text-icon-darker" icon={faHourglass} key="downtime" />,
        flags.priority && <FontAwesomeIcon className="text-text-brand" icon={faFlag} key="priority" />,
        flags.warehouseShortage && (
          <Cube className="size-4 text-surface-status-yellow-darker" key="warehouseShortage" />
        ),
        flags.overdue && <CUSTOM_ICONS.OverdueClock className="size-4 text-surface-status-rose-darker" key="overdue" />,
        flags.comment && <CUSTOM_ICONS.Comment className="size-4 text-text-secondary-darker" key="comment" />,
      ].filter(Boolean),
    [flags, nodeId],
  );

  return (
    <div
      className={cn(
        'grid h-8 grid-flow-col grid-rows-2 items-center gap-x-0.5 empty:hidden [&:has(>_:nth-child(odd):last-child)_>_:first-child]:row-span-2',
        className,
      )}
    >
      {FlagsComponents}
    </div>
  );
};
